import { uniq, difference } from 'lodash';
import type { IFSM } from '../../../../stores/model/metamodel/fsm';
import type { IState } from '../../../../stores/model/metamodel/state';
import type { ITransition } from '../../../../stores/model/metamodel/transition';
import type { IMethod } from '../../../../stores/model/metamodel/method';
import type { StateType } from '../../types';

/**
 * Class that represents a calculated state.
 *
 * Properties:
 * - first: the state from the first FSM
 * - second: the state from the second FSM
 */
class CalculatedState {
  first: IState;
  second: IState;

  constructor(first: IState, second: IState) {
    this.first = first;
    this.second = second;
  }

  /**
   * Getter for the type of the calculated state.
   */
  get type(): StateType {
    if (this.first.type !== this.second.type) {
      return 'STATE';
    } else {
      return this.first.type;
    }
  }

  /**
   * Getter for a fingerprint that can be used to check if two calculated states
   * are equal
   */
  get fingerprint(): string {
    return `${this.first.id}+${this.second.id}`;
  }
}

/**
 * Class that represents a calculated transition.
 *
 * Properties:
 * - source: the source (calculated) state
 * - target: the target (calculated) state
 * - methods: the methods for the transition
 */
class CalculatedTransition {
  source: CalculatedState;
  target: CalculatedState;
  methods: IMethod[];

  constructor(source: CalculatedState, target: CalculatedState, method: IMethod) {
    this.source = source;
    this.target = target;
    this.methods = [method];
  }

  /**
   * Add a method
   */
  addMethod(method: IMethod): void {
    if (!this.methods.includes(method)) {
      this.methods.push(method);
    }
  }
}

/**
 * Class representing a calculated FSM.
 *
 * Properties:
 * - states: the list of (calculated) states
 * - transitions: the list of (calculated) transitions
 */
class CalculatedFSM {
  states: CalculatedState[];
  transitions: CalculatedTransition[];

  constructor(states: CalculatedState[], transitions: CalculatedTransition[]) {
    this.states = states.slice();
    this.transitions = transitions.slice();
  }
}

/**
 * Utility function to get the transition for a given fsm, state and method.
 */
function getTransition(fsm: IFSM, state: IState, method: IMethod): ITransition | undefined {
  return fsm.getOutgoingTransitions(state).find((t) => t.metaMethods.includes(method));
}

/**
 * Find the calculated state for the given first and second state. Creates a new
 * one if not found.
 */
function findOrCreateState(
  states: CalculatedState[],
  first: IState,
  second: IState
): CalculatedState {
  let state = states.find((s) => s.first === first && s.second === second);
  if (state == null) {
    state = new CalculatedState(first, second);
    states.push(state);
  }
  return state;
}

/**
 * Find the calculated transition for the given source, target and method. Creates a new one if not found.
 */
function findOrCreateTransition(
  transitions: CalculatedTransition[],
  source: CalculatedState,
  target: CalculatedState,
  method: IMethod
): CalculatedTransition {
  let transition = transitions.find((t) => t.source === source && t.target === target);
  if (transition == null) {
    transition = new CalculatedTransition(source, target, method);
    transitions.push(transition);
  } else {
    transition.addMethod(method);
  }
  return transition;
}

/**
 * Calculate an FSM based on the two given FSMs.
 *
 * @param fsm1 the first FSM
 * @param fsm2 the second FSM
 *
 * @return an instance of CalculatedFSM (see above)
 */
export function calculate(fsm1: IFSM, fsm2: IFSM): CalculatedFSM {
  const states: CalculatedState[] = [];
  const transitions: CalculatedTransition[] = [];

  // step 1
  if (fsm1.initialState == null || fsm2.initialState == null) {
    return new CalculatedFSM(states, transitions);
  }
  states.push(new CalculatedState(fsm1.initialState, fsm2.initialState));

  // step 2
  const methods = uniq(fsm1.methods.slice().concat(fsm2.methods.slice()));
  const missing1 = difference(methods, fsm1.methods.slice());
  const missing2 = difference(methods, fsm2.methods.slice());

  // step 3
  let counter = 0;
  while (counter < states.length) {
    const state = states[counter];
    counter++;
    for (const method of methods) {
      const transition1 = getTransition(fsm1, state.first, method);
      const transition2 = getTransition(fsm2, state.second, method);

      let newState;
      if (transition1 != null && transition2 != null) {
        newState = findOrCreateState(states, transition1.targetState, transition2.targetState);
      } else if (transition1 != null && missing2.includes(method)) {
        newState = findOrCreateState(states, transition1.targetState, state.second);
      } else if (transition2 != null && missing1.includes(method)) {
        newState = findOrCreateState(states, state.first, transition2.targetState);
      }

      if (newState != null) {
        findOrCreateTransition(transitions, state, newState, method);
      }
    }
  }

  return new CalculatedFSM(states, transitions);
}
